import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';

type CoachResource = {
  id: string;
  coach_id: string;
  file_type: string;
  file_url: string;
  file_name: string;
  student_id: string | string[];
  created_at: string;
};

type CoachResourcesState = {
  loading: boolean;
  resources: CoachResource[];
  error: boolean;
  errorMessage: string;
};

const initialState: CoachResourcesState = {
  loading: false,
  resources: [],
  error: false,
  errorMessage: '',
};

const normalizeResources = (data: any): CoachResource[] => {
  const payload = data?.response ?? data?.data ?? data?.records ?? data ?? [];
  return Array.isArray(payload) ? (payload as CoachResource[]) : [];
};

export const getCoachResources = createAsyncThunk<CoachResource[], string>(
  'getCoachResources',
  async (coachId) => {
    if (!coachId) {
      throw new Error('Missing coach ID.');
    }
    const { data } = await api.get(`/api/coaches/${coachId}/resources`);
    return normalizeResources(data);
  }
);

const getCoachResourcesSlice = createSlice({
  name: 'getCoachResources',
  initialState,
  reducers: {
    resetCoachResources: (state) => {
      state.loading = false;
      state.resources = [];
      state.error = false;
      state.errorMessage = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCoachResources.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(getCoachResources.fulfilled, (state, action) => {
      state.loading = false;
      state.resources = action.payload;
    });
    builder.addCase(getCoachResources.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = action.error.message ?? 'Failed to load resources.';
    });
  },
});

export const { resetCoachResources } = getCoachResourcesSlice.actions;
export default getCoachResourcesSlice.reducer;
